// =============================================================================
// MASTER DATA SLICE
// Stores global reference/lookup data fetched once on app init from:
//   GET /api/common/master-data
//
// State is populated in AppWrapper on startup and remains read-only after that.
// Components read from this slice via selectors instead of maintaining their
// own local hardcoded lists.
// =============================================================================

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// =============================================================================
// STATE INTERFACE
// =============================================================================

interface MasterDataState {
  /** Location types, e.g. ['Distribution', 'Production', 'Manufacturing'] */
  locTypes: string[];
  /** Organisation types, e.g. ['PBNA', 'FLNA', '1NA'] */
  orgTypes: string[];
  /** Role types, e.g. ['DSD', 'Non-DSD', 'Fixed'] */
  roleTypes: string[];
  /** 3PL options, e.g. ['Yes', 'No', 'Any'] */
  threePLOptions: string[];
  /** DSD VP zone codes, e.g. ['BAY', 'BULK', …] */
  dsdVpZones: string[];
  /** NON-DSD VP zone codes, e.g. ['RECEIVING', 'REPLEN', …] */
  nonDsdVpZones: string[];
  /** Location facility types, e.g. ['Cross Dock', 'Drop Lot', …] */
  locationFacilityTypes: string[];
  /** True once the API call has successfully completed */
  isLoaded: boolean;
  /** Error message if the API call failed (non-fatal — app continues with defaults) */
  error: string | null;
}

const initialState: MasterDataState = {
  locTypes: [],
  orgTypes: [],
  roleTypes: [],
  threePLOptions: [],
  dsdVpZones: [],
  nonDsdVpZones: [],
  locationFacilityTypes: [],
  isLoaded: false,
  error: null,
};

// =============================================================================
// SLICE
// =============================================================================

const masterDataSlice = createSlice({
  name: 'masterData',
  initialState,

  reducers: {
    /**
     * Called in AppWrapper after a successful GET /common/master-data response.
     * Accepts the raw `data` object from the API response and maps each field.
     */
    setMasterData(
      state,
      action: PayloadAction<{
        LocTypes?: string[];
        OrgTypes?: string[];
        RoleTypes?: string[];
        '3PLOptions'?: string[];
        /** New structure: { DSD: string[], 'NON-DSD': string[] } */
        vp_zones?: { DSD?: string[]; 'NON-DSD'?: string[] };
        /** Legacy flat array (kept for backward compatibility) */
        dsd_vp_zones?: string[];
        locationFacilityType?: string[];
      }>
    ) {
      const d = action.payload;
      state.locTypes = d.LocTypes ?? state.locTypes;
      state.orgTypes = d.OrgTypes ?? state.orgTypes;
      state.roleTypes = d.RoleTypes ?? state.roleTypes;
      state.threePLOptions = d['3PLOptions'] ?? state.threePLOptions;

      // Handle new vp_zones structure { DSD: [...], 'NON-DSD': [...] }
      if (d.vp_zones) {
        state.dsdVpZones = d.vp_zones.DSD ?? state.dsdVpZones;
        state.nonDsdVpZones = d.vp_zones['NON-DSD'] ?? state.nonDsdVpZones;
      } else if (d.dsd_vp_zones) {
        // Legacy fallback: flat array was only for DSD
        state.dsdVpZones = d.dsd_vp_zones;
      }

      state.locationFacilityTypes = d.locationFacilityType ?? state.locationFacilityTypes;
      state.isLoaded = true;
      state.error = null;
    },

    /** Called when the API call fails — app continues with empty defaults. */
    setMasterDataError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      // isLoaded remains false so callers know data was not fetched
    },
  },
});

// =============================================================================
// ACTIONS
// =============================================================================

export const { setMasterData, setMasterDataError } = masterDataSlice.actions;
export default masterDataSlice.reducer;

// =============================================================================
// SELECTORS
// Using a local inline type avoids circular dependency with store/index.ts.
// =============================================================================

type SliceState = { masterData: MasterDataState };

export const selectLocTypes            = (state: SliceState) => state.masterData.locTypes;
export const selectOrgTypes            = (state: SliceState) => state.masterData.orgTypes;
export const selectRoleTypes           = (state: SliceState) => state.masterData.roleTypes;
export const selectThreePLOptions      = (state: SliceState) => state.masterData.threePLOptions;
export const selectDsdVpZones          = (state: SliceState) => state.masterData.dsdVpZones;
export const selectNonDsdVpZones       = (state: SliceState) => state.masterData.nonDsdVpZones;
export const selectLocationFacilityTypes = (state: SliceState) => state.masterData.locationFacilityTypes;
export const selectMasterDataLoaded    = (state: SliceState) => state.masterData.isLoaded;
export const selectMasterDataError     = (state: SliceState) => state.masterData.error;
